import React from 'react';
import BlogArticleBase from '../BlogArticleBase';
import { zodiacNavItems } from './zodiacNavData';

const CancerArticle = () => {
  // Données pour les fils d'Ariane structurés
  const breadcrumbs = [
    {
      name: "Accueil",
      path: "/"
    },
    {
      name: "Blog",
      path: "/blog"
    },
    {
      name: "Signes",
      path: "/blog/signes/"
    },
    {
      name: "Cancer en Astrologie",
      path: "/blog/signes/cancer"
    }
  ];

  // FAQ structurée pour le schema.org FAQPage
  const faqItems = [
    {
      question: "Quelles sont les principales caractéristiques du signe du Cancer?",
      answer: "Le Cancer est connu pour être émotif, intuitif, protecteur, sensible et attaché à la famille. Quatrième signe du zodiaque gouverné par la Lune, il se distingue par sa profondeur émotionnelle et son sens du foyer."
    },
    {
      question: "Avec quels signes le Cancer est-il compatible?",
      answer: "Le Cancer a une excellente compatibilité avec le Scorpion, les Poissons, le Taureau et la Vierge. Sa nature d'eau s'harmonise particulièrement bien avec les autres signes d'eau et de terre."
    },
    {
      question: "Dans quels domaines professionnels le Cancer excelle-t-il?",
      answer: "Le Cancer excelle dans les domaines nécessitant empathie, intuition et soins, comme la santé, l'enseignement, la gastronomie, le travail social, l'histoire et tous les métiers liés à la protection et au bien-être des autres."
    },
    {
      question: "Quelle est l'influence de la Lune sur le Cancer?",
      answer: "La Lune, astre des émotions et des cycles, confère au Cancer sa sensibilité, son intuition profonde et son attachement aux souvenirs. Elle explique leurs humeurs changeantes et leur forte connexion au passé et à la famille."
    }
  ];

  // Mots-clés optimisés pour le référencement
  const keywords = [
    "cancer signe astrologique", "caractéristiques cancer", "personnalité cancer", 
    "compatibilité cancer", "cancer et lune", "horoscope cancer", 
    "cancer élément eau", "carrière cancer", "cancer en amour"
  ];

  return (
    <BlogArticleBase
      title="Cancer : Le Signe du Protecteur - Caractéristiques et Compatibilité"
      description="Découvrez tout sur le signe astrologique du Cancer : personnalité, compatibilité amoureuse, carrière et influence de la Lune sur ce signe d'eau émotif et protecteur."
      slug="signes/cancer"
      image="/images/signes/cancer.webp"
      publishDate="2025-05-18T08:00:00Z"
      modifiedDate="2025-05-18T08:00:00Z"
      author="Valtiel"
      category="Astrologie"
      navItems={zodiacNavItems}
      navTitle="Explorer les signes du zodiaque"
      faqItems={faqItems}
      keywords={keywords}
      breadcrumbs={breadcrumbs}
      showTableOfContents={true}
    >
      {/* TL;DR - Résumé court au début */}
      <div className="article-summary">
        <p><strong>En bref:</strong> Quatrième signe du zodiaque (21 juin - 22 juillet), le Cancer est un signe d'eau gouverné par la Lune. Émotif, intuitif et protecteur, le Cancer est reconnu pour sa sensibilité et son attachement à la famille. Sa nature nourricière en fait un gardien des traditions et un créateur de foyer.</p>
      </div>

      <h2 id="definition">Le signe du Cancer : dates et caractéristiques essentielles</h2>
      <p>
        Né entre le 21 juin et le 22 juillet, le Cancer est le quatrième signe du zodiaque. 
        Représenté par le crabe, ce signe d'eau est gouverné par la Lune, l'astre des émotions et des cycles.
        Marquant le solstice d'été dans l'hémisphère nord, il symbolise le foyer, la famille et les racines émotionnelles.
      </p>
      
      <h2 id="personnalite">Traits de personnalité</h2>
      <p>
        Les Cancers sont connus pour leur nature émotive et leurs caractéristiques distinctives :
      </p>
      <ul>
        <li>Sensible et émotif</li>
        <li>Intuitif et imaginatif</li>
        <li>Protecteur et nourricier</li>
        <li>Loyal et attaché aux souvenirs</li>
        <li>Empathique et compatissant</li>
      </ul>
      
      <p>
        Cependant, ils peuvent aussi présenter certains défis :
      </p>
      <ul>
        <li>Humeurs changeantes</li>
        <li>Tendance à la mélancolie</li>
        <li>Parfois manipulateur émotionnellement</li>
        <li>Difficile à connaître (carapace protectrice)</li>
        <li>Peut être trop attaché au passé</li>
      </ul>
      
      <h2 id="compatibilite">Compatibilités amoureuses</h2>
      <p>
        Le Cancer étant un signe d'eau, il trouve naturellement une harmonie avec les autres signes d'eau ainsi qu'avec les signes de terre.
      </p>
      
      <table>
        <caption>Tableau de compatibilité du Cancer avec les autres signes</caption>
        <thead>
          <tr>
            <th>Compatibilité</th>
            <th>Signes</th>
            <th>Dynamique</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Excellente</td>
            <td>Scorpion, Poissons, Taureau, Vierge</td>
            <td>Connexion émotionnelle profonde et sécurité</td>
          </tr>
          <tr>
            <td>Bonne</td>
            <td>Capricorne, Cancer</td>
            <td>Complémentarité et valeurs familiales partagées</td>
          </tr>
          <tr>
            <td>Difficile</td>
            <td>Bélier, Balance</td>
            <td>Styles de communication et besoins différents</td>
          </tr>
          <tr>
            <td>Défi</td>
            <td>Gémeaux, Sagittaire</td>
            <td>Approches émotionnelles et valeurs opposées</td>
          </tr>
        </tbody>
      </table>
      
      <h2 id="carriere">Carrière et talents</h2>
      <p>
        Grâce à leur empathie, leur intuition et leur sens de la protection, les Cancers excellent dans plusieurs domaines :
      </p>
      <ul>
        <li>Soins de santé et médecine</li>
        <li>Éducation et enseignement</li>
        <li>Gastronomie et restauration</li>
        <li>Travail social et thérapie</li>
        <li>Histoire et préservation du patrimoine</li>
      </ul>
      
      <h2 id="planete">La Lune : Astre gouvernant</h2>
      <p>
        La Lune, satellite naturel de la Terre régissant les marées et symbolisant les émotions, influence fortement le Cancer. 
        Cette influence astrale explique leur sensibilité exceptionnelle, 
        leurs cycles d'humeur et leur profonde connexion à leur passé et à leur famille.
      </p>
      
      <h3 id="element">Élément : Eau</h3>
      <p>
        En tant que signe d'eau, le Cancer est émotif, intuitif et fluide. 
        L'eau représente les émotions, l'intuition et la profondeur psychique. Elle apporte 
        au Cancer sa sensibilité, son empathie et sa capacité à ressentir les courants émotionnels invisibles.
      </p>

      <h2 id="celebrites">Célébrités Cancer</h2>
      <p>
        De nombreuses personnalités célèbres sont nées sous le signe du Cancer, illustrant les traits caractéristiques de ce signe :
      </p>
      <ul>
        <li>Princess Diana (1er juillet)</li>
        <li>Tom Hanks (9 juillet)</li>
        <li>Meryl Streep (22 juin)</li>
        <li>Selena Gomez (22 juillet)</li>
        <li>Robin Williams (21 juillet)</li>
      </ul>

      <h2 id="conseils">Conseils pour les Cancers</h2>
      <p>
        Pour exploiter au mieux les forces du Cancer et atténuer leurs défis potentiels :
      </p>
      <ul>
        <li>Tenez un journal pour exprimer vos émotions changeantes</li>
        <li>Créez des limites saines pour ne pas absorber les énergies négatives des autres</li>
        <li>Pratiquez le lâcher-prise des souvenirs douloureux tout en honorant votre passé</li>
        <li>Équilibrez votre tendance à prendre soin des autres avec l'auto-soin</li>
        <li>Transformez votre sensibilité en créativité artistique ou en empathie constructive</li>
      </ul>

      <blockquote>
        <p>
          "Le Cancer ressent le monde avant de le comprendre. Sa force réside dans sa capacité à nourrir ce qui est fragile et à protéger ce qui est précieux, transformant une maison en foyer et des étrangers en famille." — Linda Goodman
        </p>
      </blockquote>

      <h2 id="conclusion">Conclusion</h2>
      <p>
        Le Cancer, avec sa sensibilité et son intuition, apporte au zodiaque une énergie nourricière et protectrice. 
        Sa profondeur émotionnelle et son attachement aux racines en font un signe qui valorise la connexion, 
        les traditions et la sécurité affective. 
        Comprendre l'influence de la Lune et l'élément Eau sur ce signe permet de mieux apprécier la 
        richesse et la complexité de la personnalité Cancer, gardienne de la mémoire collective et créatrice de liens durables.
      </p>
    </BlogArticleBase>
  );
};

export default CancerArticle; 